import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Cable, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { wireCostingsAPI } from '../../services/api';
import { Textarea } from '../ui/textarea';

const fmt = (n, decimals = 2) => Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: decimals, maximumFractionDigits: decimals,
});

export default function WireCostingDialog({ open, onOpenChange, part, onCreated }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    wire_diameter_min_mm: part?.wire_diameter_min_mm || '',
    wire_diameter_max_mm: part?.wire_diameter_max_mm || '',
    wire_grade: part?.wire_grade || '',
    coil_weight_kg: part?.wire_coil_weight_kg || '',
    uts_notes: part?.wire_hardness_notes || '',
    rm_cost_per_kg: 0,
    ppd_cost_per_kg: 0,
    sappd_cost_per_kg: 0,
    packing_cost_per_kg: 0,
    forwarding_cost_per_kg: 0,
    overheads_percentage: 0,
    margin_percentage: 12,
    notes: '',
  });

  useEffect(() => {
    if (open && part) {
      setForm((f) => ({
        ...f,
        wire_diameter_min_mm: part.wire_diameter_min_mm || '',
        wire_diameter_max_mm: part.wire_diameter_max_mm || '',
        wire_grade: part.wire_grade || '',
        coil_weight_kg: part.wire_coil_weight_kg || '',
        uts_notes: part.wire_hardness_notes || '',
      }));
    }
  }, [open, part]);

  // Live calculation (mirror of backend math) — INR only
  //   process = PPD + SAPPD
  //   overheads_amount = process × overheads%
  //   total = RM + PPD + SAPPD + Packing + Forwarding + overheads_amount
  //   selling_price = total × (1 + margin%)
  const calc = useMemo(() => {
    const rm = parseFloat(form.rm_cost_per_kg) || 0;
    const ppd = parseFloat(form.ppd_cost_per_kg) || 0;
    const sappd = parseFloat(form.sappd_cost_per_kg) || 0;
    const packing = parseFloat(form.packing_cost_per_kg) || 0;
    const forwarding = parseFloat(form.forwarding_cost_per_kg) || 0;
    const oh_pct = parseFloat(form.overheads_percentage) || 0;
    const process_cost = ppd + sappd;
    const overheads_amount = process_cost * (oh_pct / 100);
    const total = rm + ppd + sappd + packing + forwarding + overheads_amount;
    const mPct = parseFloat(form.margin_percentage) || 0;
    const margin = total * (mPct / 100);
    const sp = total + margin;
    return {
      process_cost_per_kg: process_cost,
      overheads_amount_per_kg: overheads_amount,
      total_cost_per_kg: total,
      margin_amount_per_kg: margin,
      selling_price_per_kg: sp,
      selling_price_per_ton: sp * 1000,
    };
  }, [form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!part?.id) { toast.error('No part selected'); return; }
    setSaving(true);
    try {
      const payload = {
        part_id: part.id,
        wire_diameter_min_mm: parseFloat(form.wire_diameter_min_mm) || null,
        wire_diameter_max_mm: parseFloat(form.wire_diameter_max_mm) || null,
        wire_grade: form.wire_grade || null,
        coil_weight_kg: parseFloat(form.coil_weight_kg) || null,
        uts_notes: form.uts_notes || null,
        rm_cost_per_kg: parseFloat(form.rm_cost_per_kg) || 0,
        ppd_cost_per_kg: parseFloat(form.ppd_cost_per_kg) || 0,
        sappd_cost_per_kg: parseFloat(form.sappd_cost_per_kg) || 0,
        packing_cost_per_kg: parseFloat(form.packing_cost_per_kg) || 0,
        forwarding_cost_per_kg: parseFloat(form.forwarding_cost_per_kg) || 0,
        overheads_percentage: parseFloat(form.overheads_percentage) || 0,
        margin_percentage: parseFloat(form.margin_percentage) || 0,
        notes: form.notes || null,
      };
      const res = await wireCostingsAPI.create(payload);
      toast.success(`Wire costing ${res.data.costing_number} created`);
      onOpenChange(false);
      if (onCreated) onCreated(res.data);
      navigate(`/costings/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create wire costing');
    } finally {
      setSaving(false);
    }
  };

  const currencySymbol = '₹';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wire-costing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cable className="h-5 w-5 text-blue-600" />
            New Wire Costing
            {part && <span className="text-base font-mono ml-2 text-muted-foreground">{part.internal_part_no}</span>}
          </DialogTitle>
          <DialogDescription>
            All values in <strong>INR per Kg</strong>. Currency conversion applies at quotation time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Wire Specs */}
          <Card><CardContent className="p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Wire Specifications</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Dia Min (mm)</Label><Input type="number" step="0.01" value={form.wire_diameter_min_mm} onChange={(e) => setField('wire_diameter_min_mm', e.target.value)} data-testid="wc-dia-min" /></div>
              <div><Label>Dia Max (mm)</Label><Input type="number" step="0.01" value={form.wire_diameter_max_mm} onChange={(e) => setField('wire_diameter_max_mm', e.target.value)} /></div>
              <div><Label>Grade</Label><Input value={form.wire_grade} onChange={(e) => setField('wire_grade', e.target.value)} placeholder="e.g., SWRH72A" /></div>
              <div><Label>Coil Weight (Kg)</Label><Input type="number" step="0.01" value={form.coil_weight_kg} onChange={(e) => setField('coil_weight_kg', e.target.value)} data-testid="wc-coil-wt" /></div>
              <div className="col-span-2"><Label>UTS / Hardness Notes</Label><Input value={form.uts_notes} onChange={(e) => setField('uts_notes', e.target.value)} placeholder="e.g., 1180–1370 MPa, HRB 80-85" /></div>
            </div>
          </CardContent></Card>

          {/* Cost components */}
          <Card><CardContent className="p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Cost Components (per Kg)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>RM Cost / Kg</Label>
                <Input type="number" step="0.01" value={form.rm_cost_per_kg} onChange={(e) => setField('rm_cost_per_kg', e.target.value)} placeholder="0.00" data-testid="wc-rm" />
              </div>
              <div>
                <Label>Margin %</Label>
                <Input type="number" step="0.1" value={form.margin_percentage} onChange={(e) => setField('margin_percentage', e.target.value)} data-testid="wc-margin" />
              </div>
              <div>
                <Label>PPD Cost / Kg <span className="text-xs text-muted-foreground">(Pickled-Phosphated-Drawn)</span></Label>
                <Input type="number" step="0.01" value={form.ppd_cost_per_kg} onChange={(e) => setField('ppd_cost_per_kg', e.target.value)} placeholder="0.00" data-testid="wc-ppd" />
              </div>
              <div>
                <Label>SAPPD Cost / Kg <span className="text-xs text-muted-foreground">(Spheroidised Annealed PP&D)</span></Label>
                <Input type="number" step="0.01" value={form.sappd_cost_per_kg} onChange={(e) => setField('sappd_cost_per_kg', e.target.value)} placeholder="0.00" data-testid="wc-sappd" />
              </div>
              <div>
                <Label>Packing Cost / Kg</Label>
                <Input type="number" step="0.01" value={form.packing_cost_per_kg} onChange={(e) => setField('packing_cost_per_kg', e.target.value)} placeholder="0.00" data-testid="wc-packing" />
              </div>
              <div>
                <Label>Forwarding Cost / Kg</Label>
                <Input type="number" step="0.01" value={form.forwarding_cost_per_kg} onChange={(e) => setField('forwarding_cost_per_kg', e.target.value)} placeholder="0.00" data-testid="wc-forwarding" />
              </div>
              <div>
                <Label>Overheads % <span className="text-xs text-muted-foreground">(of process cost: PPD + SAPPD)</span></Label>
                <Input type="number" step="0.1" value={form.overheads_percentage} onChange={(e) => setField('overheads_percentage', e.target.value)} placeholder="e.g., 25" data-testid="wc-overheads-pct" />
              </div>
            </div>
            <div>
              <Label>Notes / Special Conditions</Label>
              <Textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2} placeholder="Optional notes…" />
            </div>
          </CardContent></Card>

          {/* Cost breakdown */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4 text-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">Cost Breakdown</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 font-mono">
                <div className="flex justify-between"><span className="text-muted-foreground">Process cost:</span><span>{currencySymbol}{fmt(calc.process_cost_per_kg, 2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Overheads value:</span><span>{currencySymbol}{fmt(calc.overheads_amount_per_kg, 2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Margin value:</span><span>{currencySymbol}{fmt(calc.margin_amount_per_kg, 2)}</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Live result */}
          <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[11px] uppercase font-semibold text-slate-600">Total Cost / Kg</p>
                  <p className="text-2xl font-bold font-mono text-slate-800" data-testid="wc-total-cost-kg">{currencySymbol}{fmt(calc.total_cost_per_kg, 2)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase font-semibold text-emerald-700">Selling Price / Kg</p>
                  <p className="text-2xl font-extrabold font-mono text-emerald-700" data-testid="wc-sp-kg">{currencySymbol}{fmt(calc.selling_price_per_kg, 2)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase font-semibold text-blue-700">Price / Ton</p>
                  <p className="text-2xl font-extrabold font-mono text-blue-700">{currencySymbol}{fmt(calc.selling_price_per_ton, 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}><X className="h-4 w-4 mr-1.5" />Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="btn-primary" data-testid="wc-save-btn">
            <Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving…' : 'Save Wire Costing'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
